/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type ProductModel } from '@/api/types/products-types';
import { type ProductSkuDetails } from '@/features/product';
import { useMemo, type MouseEvent } from 'react';

export type ProductSpecification = {
  key: string;
  label: string;
  value: string | string[];
};

export type GetProductSpecificationsParams = {
  product: ProductModel | undefined;
  skuDetails: ProductSkuDetails | null | undefined;
  labels: {
    lblWarranty: string;
    lblPartNumber: string;
    lblSkuNumber: string;
    lblWeight: string;
    label_DetailsPage_body_Location: string;
    label_DetailsPage_body_ApplicationSmall: string;
    label_CategoryPage_body_Notes: string;
    label_product_bulletins: string;
    label_safety_data_sheets: string;
    label_cross_reference_num?: string;
  };
  limit?: number;
  oemPartNumber?: string;
};

// Specs intentionally omitted from the top section:
// - Part Number, SKU Number, Cross Reference Number (from productAttributes)
// - Part Number and SKU Number (from product.partNumber / product.itemId)
const HIDDEN_SPEC_KEYS = new Set(['Part Number', 'SKU Number', 'Cross Reference Number']);

export const getProductSpecifications = (
  params: GetProductSpecificationsParams,
): ProductSpecification[] => {
  const { product, skuDetails, labels, limit, oemPartNumber } = params;

  const specifications: ProductSpecification[] = [];

  if (skuDetails?.warranty) {
    specifications.push({
      key: 'Warranty',
      label: labels.lblWarranty,
      value: skuDetails.warranty,
    });
  }

  if (oemPartNumber && labels.label_cross_reference_num) {
    specifications.push({
      key: 'Cross Reference',
      label: labels.label_cross_reference_num,
      value: oemPartNumber,
    });
  }

  if (product?.itemWeight) {
    specifications.push({
      key: 'Weight',
      label: labels.lblWeight,
      value: product.itemWeight,
    });
  }

  if (product?.location) {
    specifications.push({
      key: 'Location',
      label: labels.label_DetailsPage_body_Location,
      value: product.location,
    });
  }

  if (product?.applicationNotes && product.applicationNotes.length > 0) {
    specifications.push({
      key: 'Application',
      label: labels.label_DetailsPage_body_ApplicationSmall,
      value: product.applicationNotes,
    });
  }

  const productAttributes = product?.productAttributes ?? [];
  productAttributes.forEach((item) => {
    if (item.name && item.value && !HIDDEN_SPEC_KEYS.has(item.name)) {
      specifications.push({
        key: item.name,
        label: item.name,
        value: item.value,
      });
    }
  });

  const productServiceBulletin = product?.itemPsbUrls ?? [];
  const msds =
    typeof product?.itemMsdsUrl === 'object'
      ? product?.itemMsdsUrl?.[0]
      : typeof product?.itemMsdsUrl === 'string'
        ? product?.itemMsdsUrl
        : null;

  if (productServiceBulletin.length > 0 || msds) {
    const bulletinValues: string[] = [];

    productServiceBulletin.forEach((item) => {
      if (item.title) {
        bulletinValues.push(item.title);
      }
    });

    if (msds) {
      bulletinValues.push(labels.label_safety_data_sheets);
    }

    if (bulletinValues.length > 0) {
      specifications.push({
        key: 'Product Bulletins',
        label: labels.label_product_bulletins,
        value: bulletinValues,
      });
    }
  }

  const locationSpec = specifications.find((spec) => spec.key.toLowerCase() === 'location');

  if (locationSpec && Number.isInteger(limit) && (limit as number) > 0) {
    const specsWithoutLocation = specifications.filter(
      (spec) => spec.key.toLowerCase() !== 'location',
    );
    const firstSpecs = specsWithoutLocation.slice(0, (limit as number) - 1);
    return [...firstSpecs, locationSpec];
  }

  return specifications.slice(0, limit);
};

export const calculateGap = (screenSize: 's' | 'm' | 'l', index: number) => {
  const gap = screenSize === 's' ? 3 : 2;
  return index % 2 === 0 ? 0 : gap;
};

export const handleViewAllClick = (e: MouseEvent<HTMLAnchorElement>) => {
  const element = document.querySelector('[data-scroll-target="product-specifications"]');
  if (element) {
    e.preventDefault();
    const position = element.getBoundingClientRect().top + window.pageYOffset;
    const offset = 65; //( this offset is calculated based on the mobile header height)
    window.scrollTo({
      top: position - offset,
      behavior: 'smooth',
    });

    window.dispatchEvent(new CustomEvent('product-specifications-expansion'));
  }
};

export const useProductSpecifications = (params: GetProductSpecificationsParams) => {
  const { product, skuDetails, labels, limit, oemPartNumber } = params;

  return useMemo(
    () =>
      getProductSpecifications({
        product,
        skuDetails,
        labels,
        limit,
        oemPartNumber,
      }),
    [product, skuDetails, labels, limit, oemPartNumber],
  );
};
